import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Table, Row, Col, Form, InputGroup, Button, Badge, 
    Spinner, Alert, Tooltip, OverlayTrigger, Modal, Pagination 
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";

export default function Reglements() {
    const navigate = useNavigate();
    const [reglements, setReglements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    const [filtreStatut, setFiltreStatut] = useState('tous');
    const [filtreMode, setFiltreMode] = useState('tous');
    const [showFilters, setShowFilters] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'date_reglement', direction: 'desc' });

    // États pour la pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    // États pour le modal de confirmation
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirmId, setConfirmId] = useState(null);
    const [confirmTitle, setConfirmTitle] = useState('');
    const [confirmMessage, setConfirmMessage] = useState('');
    const [confirmVariant, setConfirmVariant] = useState('primary');
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        fetchReglements();
        fetchStats();
    }, [filtreStatut, filtreMode]);

    const openConfirmModal = (id, title, message, variant = 'primary', action) => {
        setConfirmId(id);
        setConfirmTitle(title);
        setConfirmMessage(message);
        setConfirmVariant(variant);
        setConfirmAction(() => action);
        setShowConfirmModal(true);
    };

    const fetchReglements = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            let url = 'https://sawd.julesferry-ga.com/api/reglements.php';
            const params = new URLSearchParams();
            
            if (filtreStatut !== 'tous') params.append('statut', filtreStatut);
            if (filtreMode !== 'tous') params.append('mode_reglement', filtreMode);
            
            if (params.toString()) url += '?' + params.toString();
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setReglements(result.data || []);
            setCurrentPage(1); // Reset à la première page quand les données changent
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/reglements.php?action=stats',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
    };

    const showError = (message) => {
        setErrorMessage(message);
        setShowErrorAlert(true);
        setTimeout(() => setShowErrorAlert(false), 3000);
    };

    const handleValider = async (id) => {
        openConfirmModal(
            id,
            "Valider le règlement",
            "Êtes-vous sûr de vouloir valider ce règlement ? Le solde du client et de la facture sera mis à jour.",
            "success",
            async (confirmId) => {
                setIsProcessing(true);
                try {
                    const token = localStorage.getItem("token");
                    const response = await fetch(
                        'https://sawd.julesferry-ga.com/api/reglements.php',
                        {
                            method: 'POST',
                            headers: { 
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                                action: 'valider',
                                id: confirmId
                            })
                        }
                    );

                    const result = await response.json();

                    if (result.success) {
                        setShowConfirmModal(false);
                        showSuccess("Règlement validé avec succès");
                        fetchReglements();
                        fetchStats();
                    } else {
                        showError(result.message || "Erreur lors de la validation");
                    }
                } catch (err) {
                    showError("Erreur de connexion au serveur");
                } finally {
                    setIsProcessing(false);
                }
            }
        );
    };

    const handleSupprimer = async (id) => {
        openConfirmModal(
            id,
            "Confirmer la suppression",
            "Êtes-vous sûr de vouloir supprimer ce règlement ? Cette action est irréversible.",
            "danger",
            async (confirmId) => {
                setIsProcessing(true);
                try {
                    const token = localStorage.getItem("token");
                    const response = await fetch(
                        `https://sawd.julesferry-ga.com/api/reglements.php?id=${confirmId}`,
                        {
                            method: 'DELETE',
                            headers: { 'Authorization': `Bearer ${token}` }
                        }
                    );

                    const result = await response.json();

                    if (result.success) {
                        setShowConfirmModal(false);
                        showSuccess("Règlement supprimé avec succès");
                        fetchReglements();
                        fetchStats();
                    } else {
                        showError(result.message || "Erreur lors de la suppression");
                    }
                } catch (err) {
                    showError("Erreur de connexion au serveur");
                } finally {
                    setIsProcessing(false);
                }
            }
        );
    };

    const getStatutBadge = (statut) => {
        const configs = {
            en_attente: { color: 'warning', icon: 'ri-time-line', label: 'En attente' },
            valide: { color: 'success', icon: 'ri-check-double-line', label: 'Validé' },
            rejete: { color: 'danger', icon: 'ri-close-circle-line', label: 'Rejeté' }
        };
        const config = configs[statut] || configs.en_attente;
        return (
            <Badge 
                bg={config.color} 
                className="px-3 py-2"
                style={{
                    background: `linear-gradient(135deg, var(--bs-${config.color}), var(--bs-${config.color}-dark))`,
                    border: 'none'
                }}
            >
                <i className={`${config.icon} me-1`}></i>
                {config.label}
            </Badge>
        );
    };

    const getModeIcon = (mode) => {
        const icons = {
            especes: 'ri-money-cash-line',
            cheque: 'ri-bank-card-line',
            virement: 'ri-bank-line',
            carte_bancaire: 'ri-smartphone-line',
            traite: 'ri-file-copy-line'
        };
        return icons[mode] || 'ri-bank-card-line';
    };

    const getModeLabel = (mode) => {
        const labels = {
            especes: 'Espèces',
            cheque: 'Chèque',
            virement: 'Virement',
            carte_bancaire: 'Carte bancaire',
            traite: 'Traite'
        };
        return labels[mode] || mode;
    };

    const filteredReglements = useMemo(() => {
        return reglements
            .filter(r => 
                r.numero_reglement?.toLowerCase().includes(filterText.toLowerCase()) ||
                r.client_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
                r.numero_facture?.toLowerCase().includes(filterText.toLowerCase()) ||
                r.reference?.toLowerCase().includes(filterText.toLowerCase())
            )
            .sort((a, b) => {
                if (sortConfig.key === 'date_reglement') {
                    return sortConfig.direction === 'asc' 
                        ? new Date(a.date_reglement) - new Date(b.date_reglement)
                        : new Date(b.date_reglement) - new Date(a.date_reglement);
                }
                if (sortConfig.key === 'montant') {
                    return sortConfig.direction === 'asc' 
                        ? a.montant - b.montant
                        : b.montant - a.montant;
                }
                return 0;
            });
    }, [reglements, filterText, sortConfig]);

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredReglements.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredReglements.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        // Première page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        // Pages centrales
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        // Dernière page
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)}
            style={{ cursor: 'pointer' }}
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const ActionButton = ({ variant, icon, onClick, tooltip, disabled = false }) => (
        <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
            <Button variant={variant} size="sm" onClick={onClick} disabled={disabled} className="me-1">
                <i className={icon}></i>
            </Button>
        </OverlayTrigger>
    );

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <Spinner animation="border" variant="primary" />
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center">
                        <h4>{error}</h4>
                        <Button variant="outline-danger" className="mt-3" onClick={fetchReglements}>
                            <i className="ri-refresh-line me-2"></i>Réessayer
                        </Button>
                    </Alert>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* Alertes */}
                    {showSuccessAlert && (
                        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible>
                            <i className="ri-checkbox-circle-line me-2"></i>{successMessage}
                        </Alert>
                    )}
                    
                    {showErrorAlert && (
                        <Alert variant="danger" onClose={() => setShowErrorAlert(false)} dismissible>
                            <i className="ri-error-warning-line me-2"></i>{errorMessage}
                        </Alert>
                    )}

                    {/* En-tête */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h1 className="page-title mb-1">Règlements</h1>
                                    <p className="text-muted">Gérez les paiements et encaissements</p>
                                </div>
                                <Button 
                                    variant="primary" 
                                    onClick={() => navigate('/ventes/NouveauReglement')}
                                    className="px-4 py-2"
                                >
                                    <i className="ri-add-line me-2"></i>Nouveau règlement
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* Statistiques */}
                  {stats && (
    <Row className="g-3 mb-4">
        <Col sm={6} lg={6}> {/* Changé de lg={3} à lg={6} */}
            <Card className="border-0 shadow-sm">
                <Card.Body className="p-3">
                    <div className="d-flex align-items-center">
                        <div className="bg-primary bg-opacity-10 p-3 rounded-3 me-3">
                            <i className="ri-bank-card-line text-primary fs-4"></i>
                        </div>
                        <div>
                            <small className="text-muted d-block">Ce mois</small>
                            <h4 className="mb-0">{stats.mois?.nb_reglements || 0}</h4>
                            <small>{formatCurrency(stats.mois?.total_montant || 0)}</small>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </Col>
        <Col sm={6} lg={6}> {/* Changé de lg={3} à lg={6} */}
            <Card className="border-0 shadow-sm">
                <Card.Body className="p-3">
                    <div className="d-flex align-items-center">
                        <div className="bg-danger bg-opacity-10 p-3 rounded-3 me-3">
                            <i className="ri-error-warning-line text-danger fs-4"></i>
                        </div>
                        <div>
                            <small className="text-muted d-block">Impayés</small>
                            <h4 className="mb-0">{stats.impayes?.nb_factures || 0}</h4>
                            <small>{formatCurrency(stats.impayes?.total_impaye || 0)}</small>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </Col>
    </Row>
)}

                    {/* Filtres */}
                    <Card className="border-0 mb-4">
                        <Card.Body>
                            <Row className="g-3">
                                <Col md={4}>
                                    <InputGroup>
                                        <InputGroup.Text><i className="ri-search-line"></i></InputGroup.Text>
                                        <Form.Control
                                            placeholder="Rechercher..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                        />
                                    </InputGroup>
                                </Col>
                                <Col md={3}>
                                    <Form.Select value={filtreStatut} onChange={(e) => setFiltreStatut(e.target.value)}>
                                        <option value="tous">Tous les statuts</option>
                                        <option value="en_attente">En attente</option>
                                        <option value="valide">Validés</option>
                                        <option value="rejete">Rejetés</option>
                                    </Form.Select>
                                </Col>
                                <Col md={3}>
                                    <Form.Select value={filtreMode} onChange={(e) => setFiltreMode(e.target.value)}>
                                        <option value="tous">Tous les modes</option>
                                        <option value="especes">Espèces</option>
                                        <option value="cheque">Chèque</option>
                                        <option value="virement">Virement</option>
                                        <option value="carte_bancaire">Carte bancaire</option>
                                        <option value="traite">Traite</option>
                                    </Form.Select>
                                </Col>
                                <Col md={2} className="text-end">
                                    <Button variant="link" onClick={() => setShowFilters(!showFilters)}>
                                        <i className="ri-filter-line"></i> Plus de filtres
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Liste des règlements avec pagination */}
                    <Card className="border-0">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="mb-0">
                                    <thead>
                                        <tr>
                                            <th>N° Règlement</th>
                                            <SortableHeader field="date_reglement">Date</SortableHeader>
                                            <th>Client</th>
                                            <th>Facture</th>
                                            <th>Mode</th>
                                            <SortableHeader field="montant">Montant</SortableHeader>
                                            <th>Statut</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? (
                                            currentItems.map((reg) => (
                                                <tr key={reg.id_reglement}>
                                                    <td>
                                                        <Link to={`/ventes/reglements/${reg.id_reglement}`} className="text-primary">
                                                            {reg.numero_reglement}
                                                        </Link>
                                                    </td>
                                                    <td>{formatDate(reg.date_reglement)}</td>
                                                    <td>
                                                        <div>
                                                            <strong>{reg.client_nom}</strong>
                                                            <div><small className="text-muted">{reg.code_client}</small></div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {reg.numero_facture ? (
                                                            <Link to={`/ventes/facturesDetails/${reg.id_facture}`} className="text-primary">
                                                                {reg.numero_facture}
                                                            </Link>
                                                        ) : '-'}
                                                    </td>
                                                    <td>
                                                        <i className={`${getModeIcon(reg.mode_reglement)} me-2`}></i>
                                                        {getModeLabel(reg.mode_reglement)}
                                                        {reg.reference && <div><small className="text-muted">{reg.reference}</small></div>}
                                                    </td>
                                                    <td className="fw-bold">{formatCurrency(reg.montant)}</td>
                                                    <td>{getStatutBadge(reg.statut)}</td>
                                                    <td className="text-center">
                                                        <div className="d-flex justify-content-center gap-1">
                                                            <ActionButton
                                                                variant="outline-info"
                                                                icon="ri-eye-line"
                                                                onClick={() => navigate(`/ventes/reglements/${reg.id_reglement}`)}
                                                                tooltip="Voir détails"
                                                            />            
                                                            {reg.statut === 'en_attente' && (
                                                                <ActionButton
                                                                    variant="outline-success"
                                                                    icon="ri-check-double-line"
                                                                    onClick={() => handleValider(reg.id_reglement)}
                                                                    tooltip="Valider"
                                                                />
                                                            )}  
                                                            <ActionButton
                                                                variant="outline-danger"
                                                                icon="ri-delete-bin-line"
                                                                onClick={() => handleSupprimer(reg.id_reglement)}
                                                                tooltip="Supprimer"
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className="text-center py-5">
                                                    <i className="ri-bank-card-line fs-1 text-muted mb-3 d-block"></i>
                                                    <h5>Aucun règlement trouvé</h5>
                                                    <p className="text-muted">Commencez par saisir un nouveau règlement</p>
                                                    <Button variant="primary" onClick={() => navigate('/ventes/NouveauReglement')}>
                                                        <i className="ri-add-line me-2"></i>Nouveau règlement
                                                    </Button>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top">
                                    <div className="text-muted small mb-2 mb-md-0">
                                        <i className="ri-file-list-line me-1"></i>
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredReglements.length)} sur {filteredReglements.length} règlements
                                    </div>
                                    <Pagination className="mb-0 flex-wrap justify-content-center">
                                        <Pagination.First 
                                            onClick={() => handlePageChange(1)}
                                            disabled={currentPage === 1}
                                        />
                                        <Pagination.Prev 
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        />
                                        {renderPagination()}
                                        <Pagination.Next 
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        />
                                        <Pagination.Last 
                                            onClick={() => handlePageChange(totalPages)}
                                            disabled={currentPage === totalPages}
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* Modal de confirmation */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{confirmTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p dangerouslySetInnerHTML={{ __html: confirmMessage }}></p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                        Annuler
                    </Button>
                    <Button variant={confirmVariant} onClick={() => confirmAction(confirmId)} disabled={isProcessing}>
                        {isProcessing ? <Spinner size="sm" /> : 'Confirmer'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}